// components/OttoPad.tsx
// Otto 로봇 조종 패드: 바깥 고리를 4개 구역(전진/후진/좌회전/우회전)으로 나누고 가운데에 정지 원을 둔다.
//  - 고리의 구역을 누르면 그 방향 명령을 "한 번만" 보내고, 로봇은 정지 명령이 올 때까지 계속 움직인다.
//  - 가운데 [정지] 원을 누르면 정지 명령을 보낸다.
//  - 원 밖을 누르면 아무것도 하지 않는다.
//  - [댄스] 버튼을 누르면 댄스를 시작하고, 정지 원을 누를 때까지 반복한다.
//  - [AI 대화] 버튼을 누르면 로봇이 마이크로 듣고 스피커로 대답하는 대화 모드가 켜지고, 다시 누르면 꺼진다.
//    대화 내용은 아래 대화창에 나타난다.
// (수정) 방향 구역은 "누르는 순간" 명령을 보내고, 로봇은 정지 명령이 올 때까지 계속 움직인다.
//  - 속도를 쓰는 동작(걷기·굴리기의 전진/후진)만 누른 채 끄는 동안 속도를 화면에 보여 주고(점, 속도 글자),
//    손을 떼는 순간 마지막 속도를 한 번 더 보낸다. 끄는 동안에는 MQTT 를 보내지 않는다.
//  - 속도를 쓰지 않는 동작(좌회전, 우회전, 미끄러짐)은 누르는 순간 한 번만 보낸다.
//  - 가운데 [정지] 원을 누르면 정지 명령을 보낸다.
// 댄스·데모도 정지 원을 누를 때까지 계속한다.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import MQTTService from './MQTTService';
import OttoCalibration from './OttoCalibration';
import OttoCalSync from './OttoCalSync';

type Mode = 'walk' | 'roll' | 'slide';
type Dir = 'front' | 'back' | 'left' | 'right';
type Active = Dir | 'dance' | 'demo'; // 지금 하고 있는 동작 (방향, 댄스, 또는 OTTO 데모)
interface ChatLine { user: string; reply: string; }

// 운전 모드 × 방향 → 프로토콜 동작 코드 (protocol.md 의 a 값)
const ACTION_CODE: Record<Mode, Record<Dir, string>> = {
  walk: { front: 'wf', back: 'wb', left: 'wl', right: 'wr' },
  slide: { front: 'sf', back: 'sb', left: 'sl', right: 'sr' },
  roll: { front: 'rf', back: 'rb', left: 'rl', right: 'rr' },
};
const DANCE_CODE = 'dn';
const DEMO_CODE = 'od'; // OTTO 데모: 걷기 → 미끄러짐 → 굴리기 → 댄스를 반복하고, LED 데모도 같이 켜진다

const MODE_LABEL: Record<Mode, string> = { walk: '걷기 (walk)', roll: '굴리기 (roll)', slide: '미끄러짐 (slide)' };
const DIR_LABEL: Record<Dir, string> = { front: '전진', back: '후진', left: '좌회전', right: '우회전' };

// AI 대화를 시작하지 못했을 때 보드가 알려주는 이유
const AI_ERROR_TEXT: Record<string, string> = {
  no_psram: 'AI 대화를 시작하지 못했습니다. Arduino IDE 의 Tools → PSRAM 을 "OPI PSRAM" 으로 설정하고 다시 업로드하세요.',
  no_mem: 'AI 대화를 시작하지 못했습니다. 보드 메모리가 부족합니다.',
  no_key: 'AI 대화를 시작하지 못했습니다. 보드에 Gemini API 키가 없습니다. 아래 [API 키] 입력란에 키를 넣어 보드에 저장하세요.',
};

// API 키를 보드에 저장하지 못했을 때 보드가 알려주는 이유
const API_KEY_ERROR_TEXT: Record<string, string> = {
  bad_param: '키 형식이 맞지 않습니다. 영문자·숫자와 - _ . 만 쓰고 길이는 20~120자여야 합니다. 복사할 때 앞뒤에 공백이나 따옴표가 들어가지 않았는지 확인하세요.',
  nvs_fail: '보드가 키를 저장하지 못했습니다. (저장 공간 오류)',
};

const MAX_CHAT_LINES = 30; // 화면에 보관하는 대화 수

// 라디오 방송국 (보드 radio.ino 의 목록과 순서가 같아야 한다. sn = 0부터)
const RADIO_STATIONS = [
  'Classic Praha (체코 클래식)',
  'TBN 부산교통방송 (트로트/7080 가요)',
  'Power 181 (미국 Top 40 팝송)',
  'ENERGY K-Pop (한국 가요/케이팝)',
  'YourClassical Lullabies (태교/자장가 클래식)',
  'TBN 경인교통방송 (트로트/7080 가요)',
];
const RADIO_VOLUME_MAX = 21;
const RADIO_VOLUME_SEND_GAP_MS = 250; // 슬라이더를 끄는 동안 볼륨 명령 사이의 최소 간격

// 조명 색상 선택판: 벌집(육각형) 모양. 한 변에 7칸, 모두 127칸.
//  가운데 = 흰색, 바깥으로 갈수록 진해지고(채도 ↑), 맨 바깥 두 줄은 어두워진다.
//  둘레 방향 = 색상(빨강→노랑→초록→하늘→파랑→보라→분홍).
const HEX_RADIUS = 6;   // 가운데에서 바깥까지 6칸
const HEX_SIZE = 10;    // 육각형 한 칸의 크기 (viewBox 단위)

// h: 0~360, s,v: 0~1 → '#rrggbb'
function hsvToHex(h: number, s: number, v: number): string {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let r = 0, g = 0, b = 0;
  if (h < 60) { r = c; g = x; }
  else if (h < 120) { r = x; g = c; }
  else if (h < 180) { g = c; b = x; }
  else if (h < 240) { g = x; b = c; }
  else if (h < 300) { r = x; b = c; }
  else { r = c; b = x; }
  const to = (n: number) => Math.round((n + m) * 255).toString(16).padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

interface HoneyCell { color: string; points: string; }

// 육각형 칸의 꼭짓점과 색을 한 번만 계산해 둔다.
const HONEYCOMB: HoneyCell[] = (() => {
  const cells: HoneyCell[] = [];
  const w = Math.sqrt(3) * HEX_SIZE; // 칸의 가로 폭
  for (let q = -HEX_RADIUS; q <= HEX_RADIUS; q++) {
    for (let r = Math.max(-HEX_RADIUS, -q - HEX_RADIUS); r <= Math.min(HEX_RADIUS, -q + HEX_RADIUS); r++) {
      const cx = w * (q + r / 2);
      const cy = HEX_SIZE * 1.5 * r;
      const dist = Math.max(Math.abs(q), Math.abs(r), Math.abs(-q - r)); // 가운데에서 몇 번째 줄인지

      let color = '#ffffff';
      if (dist > 0) {
        const hue = (((Math.atan2(cy, cx) * 180) / Math.PI) + 360 + 0) % 360;
        const sat = Math.min(dist, 4) / 4;                 // 1~4줄: 흰색 → 진한 색
        const val = dist <= 4 ? 1 : dist === 5 ? 0.75 : 0.5; // 5~6줄: 어둡게
        color = hsvToHex(hue, sat, val);
      }

      const pts: string[] = [];
      for (let i = 0; i < 6; i++) {
        const a = ((60 * i - 30) * Math.PI) / 180;
        pts.push(`${(cx + (HEX_SIZE - 0.8) * Math.cos(a)).toFixed(2)},${(cy + (HEX_SIZE - 0.8) * Math.sin(a)).toFixed(2)}`);
      }
      cells.push({ color, points: pts.join(' ') });
    }
  }
  return cells;
})();

// 흰색 ~ 어두운 회색 (색이 없는 밝기 단계)
const LIGHT_GRAYS = ['#ffffff', '#e6e6e6', '#cccccc', '#b3b3b3', '#999999', '#808080', '#666666', '#4d4d4d', '#333333'];
const LIGHT_SEND_GAP_MS = 250; // 색·밝기를 바꾸는 동안 명령 사이의 최소 간격

// 라디오를 켜지 못했을 때 보드가 알려주는 이유
const RADIO_ERROR_TEXT: Record<string, string> = {
  no_psram: '라디오를 시작하지 못했습니다. Arduino IDE 의 Tools → PSRAM 을 "OPI PSRAM" 으로 설정하고 다시 업로드하세요.',
  connect_fail: '방송국에 연결하지 못했습니다. 다른 방송국을 골라 보세요.',
  stream_fail: '방송이 끊겨 다시 연결하지 못했습니다. 다른 방송국을 골라 보세요.',
  bad_param: '잘못된 방송국 번호입니다.',
};

// 그림(viewBox 200x200) 치수: 바깥 반지름 95, 가운데 정지 원 반지름 31
const OUTER_R = 95;
const INNER_R = 31;

// 걷기·굴리기의 전진/후진 속도(%): 가운데(정지 원 가장자리)에 가까우면 SPEED_MIN, 바깥 원주에서는 SPEED_MAX. 100이 보통 속도.
const SPEED_MIN = 40;
const SPEED_MAX = 200;
const SPEED_STEP = 10;           // 속도를 이 단위로 맞춘다 (명령이 너무 자주 가지 않게)

interface OttoPadProps {
  mac: string;
  email: string;
  mqttMessage?: string; // App 이 내려주는 최근 MQTT 메시지(JSON 문자열): 대화 기록과 AI 대화 상태를 받는다
}

const OttoPad: React.FC<OttoPadProps> = ({ mac, email, mqttMessage }) => {
  const [mode, setMode] = useState<Mode>('walk');
  const [active, setActive] = useState<Active | null>(null);
  const [speed, setSpeed] = useState<number | null>(null); // 지금 속도(%) (속도를 쓰지 않는 동작이면 null)
  const [aiOn, setAiOn] = useState(false);
  const [aiError, setAiError] = useState('');
  const [chatLog, setChatLog] = useState<ChatLine[]>([]);

  // 라디오
  const [radioOpen, setRadioOpen] = useState(false);       // 라디오 패널을 펼쳤는지
  const [radioPlaying, setRadioPlaying] = useState(false);
  const [radioStation, setRadioStation] = useState(0);
  const [radioVolume, setRadioVolume] = useState(12);
  const [radioTitle, setRadioTitle] = useState('');         // 지금 나오는 노래
  const [radioError, setRadioError] = useState('');
  const radioVolSentAt = useRef(0);
  const radioVolTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 조명
  const [calOpen, setCalOpen] = useState(false); // 보정 패널을 펼쳤는지
  const [lightOpen, setLightOpen] = useState(false);        // 조명 패널을 펼쳤는지
  const [lightOn, setLightOn] = useState(false);
  const [lightColor, setLightColor] = useState('#ff0000');  // '#rrggbb'
  const [lightPercent, setLightPercent] = useState(50);     // 밝기 1~100 (%)
  const [lightError, setLightError] = useState('');
  const [lightDemo, setLightDemo] = useState(false);        // LED 데모(무지개→점멸→흐름) 중인지
  const lightDemoRef = useRef(false);

  // Gemini API 키 (보드의 NVS 에 저장된다. 이 화면은 키를 기억하지 않는다)
  const [keyInput, setKeyInput] = useState('');              // 입력 중인 키 (보낸 뒤 바로 지운다)
  const [keySet, setKeySet] = useState<boolean | null>(null); // 보드에 키가 저장돼 있는지 (null = 아직 모름)
  const [keyMsg, setKeyMsg] = useState('');                  // 저장/삭제 결과 안내
  const [keyError, setKeyError] = useState('');
  const lightLatest = useRef({ color: '#ff0000', percent: 50 }); // 가장 최근에 고른 값
  const lightSentAt = useRef(0);
  const lightTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lightTouchedAt = useRef(0); // 마지막으로 사용자가 만진 시각 (보드 응답이 값을 되돌리지 않게 한다)

  const modeRef = useRef<Mode>('walk');
  const activeRef = useRef<Active | null>(null);
  const speedRef = useRef<number | null>(null);
  const pressedRef = useRef(false); // 패드를 누르고 있는 중인지 (끌면 속도가 바뀐다)
  const previewRef = useRef<{ dir: Dir; sp: number | null } | null>(null); // 누르고 있는 동안 고른 값 (아직 보내지 않음)
  const [touch, setTouch] = useState<{ x: number; y: number } | null>(null); // 누른 위치 (viewBox 단위, 가운데 기준)
  const aiOnRef = useRef(false);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const chatEndRef = useRef<HTMLDivElement | null>(null);

  // action: 동작 코드. stop 이 아니면 duration 0 = 정지 명령이 올 때까지 계속
  // spd: 속도(%) 40~200. 걷기·굴리기의 전진/후진에서만 보낸다. 100이 보통 속도
  // durSec: 유효시간(초). 0이면 정지 명령이 올 때까지 계속 (댄스·데모)
  const publish = useCallback((action: string, spd?: number, durSec: number = 0) => {
    if (!mac || !email) return;
    const message: Record<string, any> = { command: 'ottoAction', mac, email, action };
    if (action !== 'stop') message.duration = durSec;
    if (action !== 'stop' && spd !== undefined) message.speed = spd;
    MQTTService.getInstance().publish(`i2r/${email}/in`, JSON.stringify(message));
  }, [mac, email]);

  // AI 대화 켜기(1) / 끄기(0)
  const publishAi = useCallback((on: boolean) => {
    if (!mac || !email) return;
    MQTTService.getInstance().publish(
      `i2r/${email}/in`,
      JSON.stringify({ command: 'llmMode', mac, email, value: on ? 1 : 0 })
    );
  }, [mac, email]);

  // API 키 명령: key 문자열을 보내면 보드가 저장하고, 빈 문자열이면 삭제하고, action 'get' 이면 저장 여부만 알려준다.
  const publishApiKey = useCallback((payload: { key?: string; action?: string }) => {
    if (!mac || !email) return;
    MQTTService.getInstance().publish(
      `i2r/${email}/in`,
      JSON.stringify({ command: 'apiKey', mac, email, ...payload })
    );
  }, [mac, email]);

  // 라디오 명령: play(station) / stop / vol(volume) / get
  const publishRadio = useCallback((action: string, extra: Record<string, any> = {}) => {
    if (!mac || !email) return;
    MQTTService.getInstance().publish(
      `i2r/${email}/in`,
      JSON.stringify({ command: 'radio', mac, email, action, ...extra })
    );
  }, [mac, email]);

  // 조명 명령: sd(색상 color 'rrggbb' + 밝기 brightness 0~255로 켜기) / off / get
  const publishLight = useCallback((effect: string, extra: Record<string, any> = {}) => {
    if (!mac || !email) return;
    MQTTService.getInstance().publish(
      `i2r/${email}/in`,
      JSON.stringify({ command: 'ottoLed', mac, email, effect, ...extra })
    );
  }, [mac, email]);

  const publishRef = useRef(publish);
  publishRef.current = publish;
  const publishAiRef = useRef(publishAi);
  publishAiRef.current = publishAi;

  // 움직이는 명령을 보내기 전에, AI 대화가 켜져 있으면 먼저 끈다. (보드는 대화 중에는 동작 명령을 받지 않는다)
  const leaveAiIfOn = useCallback(() => {
    if (!aiOnRef.current) return;
    aiOnRef.current = false;
    setAiOn(false);
    publishAi(false);
  }, [publishAi]);

  // 정지는 사용자가 직접 눌렀을 때만 보낸다. 유실에 대비해 한 번 더 보낸다.
  const stopNow = useCallback(() => {
    if (activeRef.current === null) return; // 이미 정지 상태면 보내지 않는다
    activeRef.current = null;
    setActive(null);
    speedRef.current = null;
    setSpeed(null);
    pressedRef.current = false;
    previewRef.current = null;
    setTouch(null);
    publish('stop');
    setTimeout(() => publish('stop'), 150);
  }, [publish]);

  // 걷기·굴리기의 전진/후진에서만 속도를 쓴다. (좌회전, 우회전, 미끄러짐은 속도 없이 보통 동작)
  const usesSpeed = (m: Mode, d: Dir) => (m === 'walk' || m === 'roll') && (d === 'front' || d === 'back');

  // 손을 뗀 순간 고른 방향·속도를 보드로 한 번만 보낸다. 정지 명령이 올 때까지 계속 움직인다.
  const commitDir = useCallback((next: Dir, sp: number | null) => {
    if (activeRef.current !== next) leaveAiIfOn();
    activeRef.current = next;
    setActive(next);
    speedRef.current = sp;
    setSpeed(sp);
    publish(ACTION_CODE[modeRef.current][next], sp === null ? undefined : sp);
  }, [publish, leaveAiIfOn]);

  const startDance = useCallback(() => {
    if (activeRef.current === 'dance') return; // 이미 춤추는 중이면 다시 보내지 않는다
    leaveAiIfOn();
    activeRef.current = 'dance';
    setActive('dance');
    publish(DANCE_CODE);
  }, [publish, leaveAiIfOn]);

  // [보정] 버튼: 진행 중인 동작과 AI 대화를 끝내고 패널을 연다 (다시 누르면 닫는다)
  const toggleCal = useCallback(() => {
    if (calOpen) {
      setCalOpen(false);
      return;
    }
    leaveAiIfOn();
    if (activeRef.current !== null) {
      activeRef.current = null;
      setActive(null);
      publish('stop');
    }
    setCalOpen(true);
  }, [calOpen, leaveAiIfOn, publish]);

  // [OTTO 데모] 버튼: 정지 명령(가운데 정지 원)을 누를 때까지 데모를 반복한다.
  const startDemo = useCallback(() => {
    if (activeRef.current === 'demo') return; // 이미 데모 중이면 다시 보내지 않는다
    leaveAiIfOn();
    activeRef.current = 'demo';
    setActive('demo');
    publish(DEMO_CODE);
  }, [publish, leaveAiIfOn]);

  // [AI 대화] 버튼: 켜져 있으면 끄고, 꺼져 있으면 켠다. (움직이던 동작은 보드가 멈춘다)
  const toggleAi = useCallback(() => {
    setAiError('');
    if (aiOnRef.current) {
      aiOnRef.current = false;
      setAiOn(false);
      publishAi(false);
    } else {
      activeRef.current = null;
      setActive(null);
      aiOnRef.current = true;
      setAiOn(true);
      setRadioPlaying(false); // 스피커를 같이 쓰므로 보드가 라디오를 끈다
      setRadioTitle('');
      publishAi(true);
    }
  }, [publishAi]);

  // [라디오] 재생: AI 대화가 켜져 있으면 먼저 끈다. (스피커를 같이 쓰므로 보드도 대화를 끄지만 화면을 먼저 맞춘다)
  const playRadio = useCallback((station: number) => {
    setRadioError('');
    setRadioTitle('');
    if (aiOnRef.current) {
      aiOnRef.current = false;
      setAiOn(false);
      publishAi(false);
    }
    setRadioStation(station);
    setRadioPlaying(true); // 보드의 응답이 오면 실제 상태로 맞춘다
    publishRadio('play', { station });
  }, [publishAi, publishRadio]);

  const stopRadio = useCallback(() => {
    setRadioPlaying(false);
    setRadioTitle('');
    publishRadio('stop');
  }, [publishRadio]);

  // 볼륨 슬라이더: 끄는 동안에는 일정 간격으로만 보내고, 마지막 값은 반드시 보낸다.
  const changeRadioVolume = useCallback((v: number) => {
    setRadioVolume(v);
    const send = () => {
      radioVolSentAt.current = Date.now();
      radioVolTimer.current = null;
      publishRadio('vol', { volume: v });
    };
    if (radioVolTimer.current) clearTimeout(radioVolTimer.current);
    const wait = Math.max(0, RADIO_VOLUME_SEND_GAP_MS - (Date.now() - radioVolSentAt.current));
    if (wait === 0) send();
    else radioVolTimer.current = setTimeout(send, wait);
  }, [publishRadio]);

  // 가장 최근에 고른 색과 밝기를 보드로 보낸다.
  const sendLight = useCallback(() => {
    const { color, percent } = lightLatest.current;
    lightSentAt.current = Date.now();
    lightTimer.current = null;
    const brightness = Math.round((percent * 255) / 100);
    if (lightDemoRef.current) {
      publishLight('demo', { brightness }); // 데모 중에는 밝기만 바꾼다 (색은 데모가 정한다)
    } else {
      publishLight('sd', { color: color.replace('#', ''), brightness });
    }
  }, [publishLight]);

  // 보낼 시점을 정한다: 바로 보내거나, 직전 명령과의 간격이 짧으면 남은 시간 뒤에 마지막 값만 보낸다.
  const scheduleLightSend = useCallback(() => {
    if (lightTimer.current) clearTimeout(lightTimer.current);
    const wait = Math.max(0, LIGHT_SEND_GAP_MS - (Date.now() - lightSentAt.current));
    if (wait === 0) sendLight();
    else lightTimer.current = setTimeout(sendLight, wait);
  }, [sendLight]);

  // 색 또는 밝기를 바꾸면 조명을 켜고 반영한다. 끌어서 바꾸는 동안에는 일정 간격으로만 보내고, 마지막 값은 반드시 보낸다.
  const applyLight = useCallback((color: string, percent: number) => {
    setLightError('');
    lightTouchedAt.current = Date.now();
    lightLatest.current = { color, percent };
    setLightColor(color);
    setLightPercent(percent);
    setLightOn(true);
    lightDemoRef.current = false; // 색을 고르면 데모는 멈추고 그 색으로 켜진다
    setLightDemo(false);
    scheduleLightSend();
  }, [scheduleLightSend]);

  // 밝기 슬라이더: 데모 중이면 데모를 유지한 채 밝기만 바꾸고, 아니면 지금 색으로 켠다.
  const changeLightBrightness = useCallback((percent: number) => {
    if (!lightDemoRef.current) {
      applyLight(lightLatest.current.color, percent);
      return;
    }
    setLightError('');
    lightTouchedAt.current = Date.now();
    lightLatest.current = { ...lightLatest.current, percent };
    setLightPercent(percent);
    scheduleLightSend();
  }, [applyLight, scheduleLightSend]);

  const turnOffLight = useCallback(() => {
    if (lightTimer.current) { clearTimeout(lightTimer.current); lightTimer.current = null; }
    lightTouchedAt.current = Date.now();
    lightDemoRef.current = false;
    setLightDemo(false);
    setLightOn(false);
    publishLight('off');
  }, [publishLight]);

  // [LED 데모] 버튼: 누르면 데모를 시작하고, 데모 중에 누르면 멈추고 끈다.
  const toggleLightDemo = useCallback(() => {
    setLightError('');
    lightTouchedAt.current = Date.now();
    if (lightDemoRef.current) {
      turnOffLight();
      return;
    }
    if (lightTimer.current) { clearTimeout(lightTimer.current); lightTimer.current = null; }
    lightDemoRef.current = true;
    setLightDemo(true);
    setLightOn(false);
    lightSentAt.current = Date.now();
    publishLight('demo', { brightness: Math.round((lightLatest.current.percent * 255) / 100) });
  }, [publishLight, turnOffLight]);

  // [저장] 버튼: 입력한 키를 보드로 보내고, 입력칸은 바로 지운다.
  const saveApiKey = useCallback(() => {
    const k = keyInput.trim();
    if (!k) { setKeyError('키를 입력하세요.'); return; }
    setKeyError('');
    setKeyMsg('보드로 보내는 중...');
    publishApiKey({ key: k });
    setKeyInput(''); // 화면에 남기지 않는다
  }, [keyInput, publishApiKey]);

  const deleteApiKey = useCallback(() => {
    if (!window.confirm('보드에 저장된 Gemini API 키를 삭제할까요? 삭제하면 AI 대화를 쓸 수 없습니다.')) return;
    setKeyError('');
    setKeyMsg('삭제하는 중...');
    publishApiKey({ key: '' });
  }, [publishApiKey]);

  // 이 화면에 들어오면 보드에 키가 저장돼 있는지만 한 번 물어본다. (키 자체는 보드가 알려 주지 않는다)
  useEffect(() => {
    publishApiKey({ action: 'get' });
  }, [publishApiKey]);

  // 이 화면에 들어오면 보드의 조명 상태를 한 번 물어본다.
  useEffect(() => {
    publishLight('get');
    return () => { if (lightTimer.current) clearTimeout(lightTimer.current); };
  }, [publishLight]);

  // 이 화면에 들어오면 보드의 라디오 상태(재생 중인지, 방송국, 볼륨)를 한 번 물어본다.
  useEffect(() => {
    publishRadio('get');
    return () => { if (radioVolTimer.current) clearTimeout(radioVolTimer.current); };
  }, [publishRadio]);

  // 운전 모드를 바꾸면 안전을 위해 먼저 정지한다. (다시 방향을 눌러야 새 모드로 움직인다)
  useEffect(() => {
    modeRef.current = mode;
    if (activeRef.current !== null) stopNow();
  }, [mode, stopNow]);

  // 이 화면을 떠날 때(다른 페이지로 이동) 움직이거나 대화 중이면 정지/종료를 보낸다.
  useEffect(() => {
    return () => {
      if (activeRef.current !== null) {
        activeRef.current = null;
        publishRef.current('stop');
      }
      if (aiOnRef.current) {
        aiOnRef.current = false;
        publishAiRef.current(false);
      }
    };
  }, []);

  // 보드가 보내는 AI 대화 응답(lm)과 대화 기록(lr)을 받는다.
  useEffect(() => {
    if (!mqttMessage) return;
    try {
      const m = JSON.parse(mqttMessage);
      if (m.mac !== mac) return;
      if (m.command === 'llmReply' && (m.userText || m.text)) {
        setChatLog((prev) => [...prev, { user: String(m.userText || ''), reply: String(m.text || '') }].slice(-MAX_CHAT_LINES));
      } else if (m.command === 'apiKey') {
        if (m.ok === 0 || m.ok === false) {
          setKeyMsg('');
          setKeyError(API_KEY_ERROR_TEXT[m.err] || `API 키를 저장하지 못했습니다. (${m.err || '알 수 없는 오류'})`);
        } else {
          setKeyError('');
          setKeySet(m.has === 1);
          setKeyMsg((prev) => (prev === '보드로 보내는 중...'
            ? (m.keyValid === 1 ? '보드에 저장했고, Gemini가 키를 확인했습니다.'
              : m.keyValid === 0 ? '보드에 저장했지만 Gemini가 키를 거부했습니다. 키를 다시 확인하세요.'
              : '보드에 저장했습니다. (키 확인은 하지 못했습니다)')
            : prev === '삭제하는 중...' ? '삭제했습니다.' : prev));
        }
      } else if (m.command === 'ottoLed') {
        if (m.ok === 0 || m.ok === false) {
          setLightError(m.err === 'bad_param' ? '색상 형식이 맞지 않습니다.' : `조명 오류 (${m.err || '알 수 없음'})`);
        } else {
          // 보드가 알려 준 지금 상태: off 꺼짐 / sd 단색 켜짐 / demo LED 데모 중
          const demo = m.effect === 'demo';
          lightDemoRef.current = demo;
          setLightDemo(demo);
          if (m.effect === 'off' || demo) setLightOn(false);
          else if (m.effect === 'sd') setLightOn(true);
          // 사용자가 방금 만졌으면 보드가 되돌려 준 옛 값으로 덮어쓰지 않는다
          if (Date.now() - lightTouchedAt.current > 800) {
            if (typeof m.color === 'string' && /^[0-9a-fA-F]{6}$/.test(m.color)) {
              const c = '#' + m.color.toLowerCase();
              setLightColor(c);
              lightLatest.current.color = c;
            }
            if (typeof m.brightness === 'number') {
              const p = Math.min(100, Math.max(1, Math.round((m.brightness * 100) / 255)));
              setLightPercent(p);
              lightLatest.current.percent = p;
            }
          }
        }
      } else if (m.command === 'radio') {
        if (m.action === 'title') { // 노래가 바뀌었다
          setRadioTitle(String(m.text || ''));
        } else if (m.ok === 0 || m.ok === false) { // 실패
          setRadioPlaying(m.playing === 1);
          setRadioError(RADIO_ERROR_TEXT[m.err] || `라디오 오류 (${m.err || '알 수 없음'})`);
        } else {
          if (m.playing !== undefined) setRadioPlaying(m.playing === 1);
          if (m.playing === 0) setRadioTitle('');
          if (typeof m.station === 'number') setRadioStation(m.station);
          // 볼륨 명령의 응답은 우리가 보낸 값의 되돌림이라 무시한다. (슬라이더를 끄는 중에 옛 값으로 튀지 않게)
          if (typeof m.volume === 'number' && m.action !== 'vol') setRadioVolume(m.volume);
        }
      } else if (m.command === 'llmMode') {
        if (m.ok === 0 || m.ok === false) { // 켜지 못했다
          aiOnRef.current = false;
          setAiOn(false);
          setAiError(AI_ERROR_TEXT[m.err] || `AI 대화를 시작하지 못했습니다. (${m.err || '알 수 없는 오류'})`);
        } else {
          aiOnRef.current = !!m.value;
          setAiOn(!!m.value);
        }
      }
    } catch (_) { /* 무시 */ }
  }, [mqttMessage, mac]);

  // 대화가 추가되면 맨 아래로 스크롤한다.
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ block: 'nearest' });
  }, [chatLog]);

  // 누른 위치: 가운데에서의 거리(viewBox 단위)와 방향 구역
  const padPos = (e: React.PointerEvent): { dist: number; dir: Dir; dx: number; dy: number } | null => {
    const el = svgRef.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    const scale = r.width / 200; // 화면 크기 → viewBox 단위
    const dx = (e.clientX - (r.left + r.width / 2)) / scale;
    const dy = (e.clientY - (r.top + r.height / 2)) / scale;
    const dist = Math.hypot(dx, dy);
    const a = (Math.atan2(-dy, dx) * 180) / Math.PI; // 위쪽이 90도
    let dir: Dir;
    if (a >= 45 && a < 135) dir = 'front';
    else if (a >= 135 || a < -135) dir = 'left';
    else if (a >= -135 && a < -45) dir = 'back';
    else dir = 'right';
    return { dist, dir, dx, dy };
  };

  // 누른 위치 점: 원 밖으로 나가도 테두리 안쪽에 붙여 표시한다.
  const showTouch = (p: { dist: number; dx: number; dy: number }) => {
    const k = p.dist > OUTER_R ? OUTER_R / p.dist : 1;
    setTouch({ x: p.dx * k, y: p.dy * k });
  };

  // 누른 위치 → 정지 원 / 고리의 구역 / 원 밖(무시)
  const hitTest = (e: React.PointerEvent): Dir | 'stop' | 'outside' => {
    const p = padPos(e);
    if (!p || p.dist > OUTER_R) return 'outside';
    if (p.dist <= INNER_R) return 'stop';
    return p.dir;
  };

  // 가운데에서의 거리 → 속도(%): 정지 원 가장자리 SPEED_MIN(40) … 바깥 원주 SPEED_MAX(200)
  const speedFromDist = (dist: number): number => {
    const t = Math.min(1, Math.max(0, (dist - INNER_R) / (OUTER_R - INNER_R)));
    const raw = SPEED_MIN + (SPEED_MAX - SPEED_MIN) * t;
    return Math.min(SPEED_MAX, Math.max(SPEED_MIN, Math.round(raw / SPEED_STEP) * SPEED_STEP));
  };

  // 누르고 끄는 동안(속도를 쓰는 동작만): 화면에만 반영한다 (속도 글자, 점). MQTT 는 보내지 않는다.
  const previewSpeed = (dist: number) => {
    const pv = previewRef.current;
    if (!pv) return;
    const sp = speedFromDist(dist);
    previewRef.current = { dir: pv.dir, sp };
    setSpeed(sp);
  };

  // 누르던 것을 취소하고 화면을 실제 로봇 상태로 되돌린다.
  const cancelPreview = useCallback(() => {
    pressedRef.current = false;
    previewRef.current = null;
    setTouch(null);
    setActive(activeRef.current);
    setSpeed(speedRef.current);
  }, []);

  // 누르는 순간 그 방향(속도를 쓰는 동작이면 누른 위치의 속도)으로 명령을 보낸다.
  const onDown = (e: React.PointerEvent<SVGSVGElement>) => {
    const hit = hitTest(e);
    if (hit === 'outside') return; // 원 밖: 아무것도 하지 않는다
    if (hit === 'stop') { stopNow(); return; }
    const p = padPos(e);
    pressedRef.current = true;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* 무시 */ }
    if (p) showTouch(p);
    const sp = usesSpeed(modeRef.current, hit) ? speedFromDist(p ? p.dist : INNER_R) : null;
    previewRef.current = { dir: hit, sp };
    commitDir(hit, sp);
  };

  // 끄는 동안: 방향은 누른 구역으로 고정하고, 속도를 쓰는 동작만 속도를 화면에 보여 준다.
  const onMove = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!pressedRef.current || !previewRef.current) return;
    const p = padPos(e);
    if (!p) return;
    showTouch(p);
    if (usesSpeed(modeRef.current, previewRef.current.dir)) previewSpeed(p.dist);
  };

  // 손을 떼면: 속도를 쓰는 동작은 마지막 속도가 처음 보낸 값과 다를 때 한 번 더 보낸다.
  const onUp = () => {
    if (!pressedRef.current) return;
    const pv = previewRef.current;
    pressedRef.current = false;
    previewRef.current = null;
    setTouch(null);
    if (pv && pv.sp !== null && pv.sp !== speedRef.current) commitDir(pv.dir, pv.sp);
  };

  // 터치가 취소되거나, 누른 채로 다른 앱·탭으로 가면 보내지 않고 취소한다.
  useEffect(() => {
    const onVis = () => { if (document.hidden) cancelPreview(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('blur', cancelPreview);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('blur', cancelPreview);
    };
  }, [cancelPreview]);

  const fill = (d: Dir) => (active === d ? 'url(#ottoActiveGrad)' : 'url(#ottoIdleGrad)');
  const textFill = (d: Dir) => (active === d ? '#ffffff' : '#1e293b');
  const subTextFill = (d: Dir) => (active === d ? 'rgba(255, 255, 255, 0.88)' : '#64748b');

  const statusText =
    aiOn ? 'AI 대화 중 (로봇에게 말을 걸어 보세요)'
    : active === null ? '정지'
    : active === 'dance' ? '댄스 중'
    : active === 'demo' ? 'OTTO 데모 중 (걷기 → 미끄러짐 → 굴리기 → 댄스 반복)'
    : `${DIR_LABEL[active]} 중 (${MODE_LABEL[mode]})${speed !== null ? ` · 속도 ${speed}%` : ''}`;

  return (
    <div className="detail-card otto-card" style={{ marginBottom: '24px' }}>
      <OttoCalSync mac={mac} email={email} mqttMessage={mqttMessage} />

      <div className="otto-toolbar">
        <div className="otto-mode-group" role="group" aria-label="운전 모드">
          {(['walk', 'roll', 'slide'] as Mode[]).map((m) => (
            <button
              key={m}
              type="button"
              className={`action-btn ${mode === m ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setMode(m)}
            >
              {MODE_LABEL[m]}
            </button>
          ))}
        </div>

        <div className="otto-feature-group">
          <button
            type="button"
            className={`action-btn ${active === 'dance' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={startDance}
          >
            <span className="otto-dot" />
            댄스 (dance)
          </button>
          <button
            type="button"
            className={`action-btn ${aiOn ? 'btn-primary' : 'btn-secondary'}`}
            onClick={toggleAi}
          >
            <span className="otto-dot" />
            {aiOn ? 'AI 대화 끄기' : 'AI 대화'}
          </button>
          <button
            type="button"
            className={`action-btn ${radioPlaying || radioOpen ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setRadioOpen((open) => !open)}
          >
            <span className="otto-dot" />
            라디오{radioPlaying ? ' (재생 중)' : ''}
          </button>
          <button
            type="button"
            className={`action-btn ${lightOn || lightOpen ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setLightOpen((open) => !open)}
          >
            <span className="otto-dot" />
            조명{lightOn ? ' (켜짐)' : ''}
          </button>
          <button
            type="button"
            className={`action-btn ${lightDemo ? 'btn-primary' : 'btn-secondary'}`}
            onClick={toggleLightDemo}
          >
            <span className="otto-dot" />
            {lightDemo ? 'LED 데모 중지' : 'LED 데모'}
          </button>
          <button
            type="button"
            className={`action-btn ${active === 'demo' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={startDemo}
          >
            <span className="otto-dot" />
            OTTO 데모
          </button>
          <button
            type="button"
            className={`action-btn ${calOpen ? 'btn-primary' : 'btn-secondary'}`}
            onClick={toggleCal}
          >
            <span className="otto-dot" />
            로봇 맞추기
          </button>
        </div>
      </div>

      {(aiOn || !!aiError) && ( // [AI 대화]를 누르면(또는 키가 없어 시작하지 못하면) 나타난다
        <div className="otto-subpanel">
          <div className="otto-subpanel-title">API 키 (Gemini)</div>
          <div style={{ fontSize: '0.86rem', color: '#64748b', marginBottom: '12px', lineHeight: 1.5 }}>
            AI 대화에 쓰는 Gemini API 키를 보드에 저장합니다. 한 번 저장하면 보드를 껐다 켜도 남아 있고,
            펌웨어 파일에는 키가 들어 있지 않습니다. 이 화면도 키를 기억하지 않습니다.
          </div>

          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            padding: '5px 12px', borderRadius: '999px', fontSize: '0.84rem', fontWeight: 600,
            marginBottom: '12px',
            background: keySet ? '#dcfce7' : '#f1f5f9',
            color: keySet ? '#15803d' : '#475569'
          }}>
            {keySet === null ? '보드의 키 상태를 확인하는 중...' : keySet ? '✅ 보드에 키가 저장되어 있습니다' : '⚠ 보드에 저장된 키가 없습니다'}
          </div>

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              type="password"
              value={keyInput}
              onChange={(e) => { setKeyInput(e.target.value); setKeyError(''); }}
              placeholder="Gemini API 키를 붙여 넣으세요"
              autoComplete="off"
              spellCheck={false}
              style={{
                flex: '1 1 260px', minWidth: 0, padding: '10px 14px',
                border: '1px solid #cbd5e1', borderRadius: '12px', fontSize: '0.9rem',
                background: '#f8fafc', outline: 'none'
              }}
            />
            <button type="button" className="action-btn btn-primary" onClick={saveApiKey} disabled={!keyInput.trim()}>
              보드에 저장
            </button>
            {keySet && (
              <button type="button" className="action-btn" onClick={deleteApiKey}
                style={{ backgroundColor: '#ef4444', color: '#ffffff', border: 'none' }}>
                삭제
              </button>
            )}
          </div>

          {keyMsg && <div style={{ marginTop: '10px', color: '#2563eb', fontSize: '0.9rem', fontWeight: 500 }}>{keyMsg}</div>}
          {keyError && (
            <div style={{ marginTop: '10px', padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px', color: '#b91c1c', fontSize: '0.88rem' }}>
              {keyError}
            </div>
          )}

          <div style={{ marginTop: '10px', fontSize: '0.8rem', color: '#94a3b8' }}>
            키는 MQTT 로 보드에 전달됩니다. 같은 브로커에 접속하는 다른 사람이 메시지를 볼 수 없도록 브로커 접근 권한을 설정해 두는 것이 안전합니다.
          </div>
        </div>
      )}

      {calOpen && (
        <OttoCalibration
          mac={mac}
          email={email}
          mqttMessage={mqttMessage}
          onClose={() => setCalOpen(false)}
        />
      )}

      {lightOpen && (
        <div className="otto-subpanel">
          <div className="otto-subpanel-title">조명</div>

          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '16px' }}>
            <label htmlFor="otto-light-color" style={{ fontWeight: 600, color: '#334155', fontSize: '0.9rem' }}>색상</label>
            <input
              id="otto-light-color"
              type="color"
              value={lightColor}
              onChange={(e) => applyLight(e.target.value, lightPercent)}
              style={{ width: '56px', height: '38px', padding: '2px', border: '1px solid #cbd5e1', borderRadius: '10px', background: '#ffffff', cursor: 'pointer' }}
              title="눌러서 색을 고르세요"
            />
            <span style={{
              fontVariantNumeric: 'tabular-nums', color: '#475569', fontSize: '0.86rem',
              background: '#f1f5f9', padding: '4px 10px', borderRadius: '8px', fontWeight: 600
            }}>
              {lightColor}
            </span>
          </div>

          {/* 벌집 모양 색상 선택판: 칸을 누르면 그 색으로 조명이 켜진다 */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '14px' }}>
            <svg
              viewBox={`${-1.9 * HEX_SIZE * HEX_RADIUS - 6} ${-1.5 * HEX_SIZE * HEX_RADIUS - HEX_SIZE - 1} ${3.8 * HEX_SIZE * HEX_RADIUS + 12} ${3 * HEX_SIZE * HEX_RADIUS + 2 * HEX_SIZE + 2}`}
              style={{ width: '100%', maxWidth: '300px', touchAction: 'manipulation', userSelect: 'none', WebkitUserSelect: 'none' }}
              role="group"
              aria-label="색상 선택판"
            >
              {HONEYCOMB.map((cell, i) => {
                const selected = lightOn && cell.color === lightColor;
                return (
                  <polygon
                    key={i}
                    points={cell.points}
                    fill={cell.color}
                    stroke={selected ? '#0f172a' : cell.color}
                    strokeWidth={selected ? 2 : 0.5}
                    style={{ cursor: 'pointer' }}
                    onClick={() => applyLight(cell.color, lightPercent)}
                  />
                );
              })}
            </svg>
          </div>

          {/* 흰색 ~ 회색 */}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '16px' }}>
            {LIGHT_GRAYS.map((c) => (
              <button
                key={c}
                type="button"
                aria-label={`회색 ${c}`}
                onClick={() => applyLight(c, lightPercent)}
                style={{
                  width: '28px', height: '28px', borderRadius: '50%', cursor: 'pointer', background: c,
                  border: lightOn && lightColor === c ? '3px solid #2563eb' : '1px solid #cbd5e1',
                  boxShadow: lightOn && lightColor === c ? '0 0 0 3px rgba(37, 99, 235, 0.2)' : '0 1px 2px rgba(0,0,0,0.06)',
                  transition: 'all 0.15s ease'
                }}
              />
            ))}
          </div>

          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px', background: '#f8fafc', padding: '10px 14px', borderRadius: '12px' }}>
            <label htmlFor="otto-light-brightness" style={{ fontWeight: 600, color: '#334155', fontSize: '0.9rem', whiteSpace: 'nowrap' }}>밝기</label>
            <input
              id="otto-light-brightness"
              type="range"
              min={1}
              max={100}
              step={1}
              value={lightPercent}
              onChange={(e) => changeLightBrightness(Number(e.target.value))}
              style={{ flex: 1, minWidth: 0 }}
            />
            <span style={{ width: '48px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: '#1e293b' }}>{lightPercent}%</span>
          </div>

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            {lightOn ? (
              <button type="button" className="action-btn" onClick={turnOffLight}
                style={{ backgroundColor: '#ef4444', color: '#ffffff', border: 'none' }}>
                끄기
              </button>
            ) : (
              <button type="button" className="action-btn btn-primary" onClick={() => applyLight(lightColor, lightPercent)}>
                켜기
              </button>
            )}
            <span style={{ color: lightOn ? '#2563eb' : '#64748b', fontSize: '0.88rem', fontWeight: 500 }}>
              {lightDemo ? 'LED 데모 중입니다 (밝기는 조절할 수 있고, 색을 고르면 데모가 멈춥니다)'
                : lightOn ? '조명이 켜져 있습니다' : '조명이 꺼져 있습니다. 색을 고르면 켜집니다.'}
            </span>
          </div>

          {lightError && (
            <div style={{ marginTop: '10px', padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px', color: '#b91c1c', fontSize: '0.88rem' }}>
              {lightError}
            </div>
          )}
        </div>
      )}

      {radioOpen && (
        <div className="otto-subpanel">
          <div className="otto-subpanel-title">라디오</div>

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '14px' }}>
            <label htmlFor="otto-radio-station" style={{ fontWeight: 600, color: '#334155', fontSize: '0.9rem' }}>방송국</label>
            <select
              id="otto-radio-station"
              value={radioStation}
              onChange={(e) => {
                const idx = Number(e.target.value);
                setRadioStation(idx);
                if (radioPlaying) playRadio(idx); // 듣는 중에 고르면 바로 그 방송국으로 바꾼다
              }}
              style={{
                flex: '1 1 220px', minWidth: 0, padding: '10px 12px',
                border: '1px solid #cbd5e1', borderRadius: '12px', background: '#f8fafc',
                fontSize: '0.9rem', color: '#1e293b', outline: 'none'
              }}
            >
              {RADIO_STATIONS.map((name, i) => (
                <option key={i} value={i}>{i + 1}. {name}</option>
              ))}
            </select>
            {radioPlaying ? (
              <button type="button" className="action-btn" onClick={stopRadio}
                style={{ backgroundColor: '#ef4444', color: '#ffffff', border: 'none' }}>
                정지
              </button>
            ) : (
              <button type="button" className="action-btn btn-primary" onClick={() => playRadio(radioStation)}>
                재생
              </button>
            )}
          </div>

          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', background: '#f8fafc', padding: '10px 14px', borderRadius: '12px' }}>
            <label htmlFor="otto-radio-volume" style={{ fontWeight: 600, color: '#334155', fontSize: '0.9rem', whiteSpace: 'nowrap' }}>볼륨</label>
            <input
              id="otto-radio-volume"
              type="range"
              min={0}
              max={RADIO_VOLUME_MAX}
              step={1}
              value={radioVolume}
              onChange={(e) => changeRadioVolume(Number(e.target.value))}
              style={{ flex: 1, minWidth: 0 }}
            />
            <span style={{ width: '56px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: '#1e293b' }}>
              {radioVolume} / {RADIO_VOLUME_MAX}
            </span>
          </div>

          <div style={{ marginTop: '12px', fontSize: '0.88rem', fontWeight: 500, color: radioPlaying ? '#2563eb' : '#64748b' }}>
            {radioPlaying ? `재생 중: ${RADIO_STATIONS[radioStation]}` : '정지'}
            {radioPlaying && radioTitle && <div style={{ marginTop: '4px', fontWeight: 600 }}>♪ {radioTitle}</div>}
          </div>

          {radioError && (
            <div style={{ marginTop: '10px', padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px', color: '#b91c1c', fontSize: '0.88rem' }}>
              {radioError}
            </div>
          )}
        </div>
      )}

      <div className="otto-pad-stage">
        <div className="otto-pad-wrapper">
          <svg
            ref={svgRef}
            viewBox="0 0 200 200"
            className="otto-pad-svg"
            onPointerDown={onDown}
            onPointerMove={onMove}
            onPointerUp={onUp}
            onPointerCancel={cancelPreview}
            onContextMenu={(e) => e.preventDefault()}
          >
            <defs>
              <linearGradient id="ottoIdleGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#ffffff" />
                <stop offset="100%" stopColor="#f1f5f9" />
              </linearGradient>
              <linearGradient id="ottoActiveGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#3b82f6" />
                <stop offset="100%" stopColor="#1d4ed8" />
              </linearGradient>
              <linearGradient id="ottoStopGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#fff1f2" />
                <stop offset="100%" stopColor="#ffe4e6" />
              </linearGradient>
              <linearGradient id="ottoStopActiveGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#f43f5e" />
                <stop offset="100%" stopColor="#dc2626" />
              </linearGradient>
              <filter id="ottoCenterShadow" x="-25%" y="-25%" width="150%" height="150%">
                <feDropShadow dx="0" dy="3" stdDeviation="4" floodColor="#0f172a" floodOpacity="0.14" />
              </filter>
            </defs>

            {/* 고리의 4개 구역 (바깥 반지름 95, 안쪽 반지름 31) */}
            <path
              className="otto-sector"
              d="M121.92 78.08 L167.18 32.82 A95 95 0 0 0 32.82 32.82 L78.08 78.08 A31 31 0 0 1 121.92 78.08 Z"
              fill={fill('front')}
              stroke="#cbd5e1"
              strokeWidth="1.5"
            />
            <path
              className="otto-sector"
              d="M78.08 78.08 L32.82 32.82 A95 95 0 0 0 32.82 167.18 L78.08 121.92 A31 31 0 0 1 78.08 78.08 Z"
              fill={fill('left')}
              stroke="#cbd5e1"
              strokeWidth="1.5"
            />
            <path
              className="otto-sector"
              d="M78.08 121.92 L32.82 167.18 A95 95 0 0 0 167.18 167.18 L121.92 121.92 A31 31 0 0 1 78.08 121.92 Z"
              fill={fill('back')}
              stroke="#cbd5e1"
              strokeWidth="1.5"
            />
            <path
              className="otto-sector"
              d="M121.92 121.92 L167.18 167.18 A95 95 0 0 0 167.18 32.82 L121.92 78.08 A31 31 0 0 1 121.92 121.92 Z"
              fill={fill('right')}
              stroke="#cbd5e1"
              strokeWidth="1.5"
            />

            {/* 속도 단계 가이드 호 (걷기/굴리기 모드일 때 은은하게 표시) */}
            {(mode === 'walk' || mode === 'roll') && (
              <g fill="none" strokeWidth="1" strokeDasharray="2.5 3.5" style={{ pointerEvents: 'none' }}>
                <path d="M55.45 55.45 A63 63 0 0 1 144.55 55.45" stroke={active === 'front' ? 'rgba(255,255,255,0.35)' : '#cbd5e1'} />
                <path d="M144.55 144.55 A63 63 0 0 1 55.45 144.55" stroke={active === 'back' ? 'rgba(255,255,255,0.35)' : '#cbd5e1'} />
              </g>
            )}

            {/* 바깥 테두리 링 */}
            <circle cx="100" cy="100" r={OUTER_R} fill="none" stroke="#94a3b8" strokeWidth="2" style={{ pointerEvents: 'none' }} />

            {/* 방향 화살표 아이콘 */}
            <g fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" style={{ pointerEvents: 'none' }}>
              <path d="M95 27 L100 22 L105 27" stroke={active === 'front' ? '#ffffff' : '#94a3b8'} />
              <path d="M95 173 L100 178 L105 173" stroke={active === 'back' ? '#ffffff' : '#94a3b8'} />
              <path d="M18 95 L13 100 L18 105" stroke={active === 'left' ? '#ffffff' : '#94a3b8'} />
              <path d="M182 95 L187 100 L182 105" stroke={active === 'right' ? '#ffffff' : '#94a3b8'} />
            </g>

            {/* 누른 위치 표시 점 */}
            {touch && (
              <g style={{ pointerEvents: 'none' }}>
                <circle cx={100 + touch.x} cy={100 + touch.y} r="11" fill="rgba(255,255,255,0.35)" stroke="#ffffff" strokeWidth="1.5" />
                <circle cx={100 + touch.x} cy={100 + touch.y} r="5" fill="#0f172a" stroke="#ffffff" strokeWidth="1.5" />
                {speed !== null && (
                  <text
                    x={100 + touch.x}
                    y={100 + touch.y + (touch.y < -60 ? 26 : -16)} // 위쪽 가장자리에서는 점 아래에 표시
                    textAnchor="middle"
                    fontSize="11"
                    fontWeight="800"
                    fill="#0f172a"
                    stroke="#ffffff"
                    strokeWidth="3"
                    paintOrder="stroke"
                  >
                    {speed}%
                  </text>
                )}
              </g>
            )}

            {/* 가운데 정지 원 */}
            <g className="otto-stop-group">
              <circle cx="100" cy="100" r={INNER_R + 2.5} fill="#ffffff" style={{ pointerEvents: 'none' }} />
              <circle
                cx="100"
                cy="100"
                r={INNER_R}
                fill={active !== null ? 'url(#ottoStopActiveGrad)' : 'url(#ottoStopGrad)'}
                stroke={active !== null ? '#dc2626' : '#fda4af'}
                strokeWidth="2"
                filter="url(#ottoCenterShadow)"
              />
              <circle
                cx="100"
                cy="100"
                r={INNER_R - 4.5}
                fill="none"
                stroke={active !== null ? 'rgba(255,255,255,0.35)' : 'rgba(244,63,94,0.25)'}
                strokeWidth="1"
                style={{ pointerEvents: 'none' }}
              />
            </g>

            <g fontSize="13.5" fontWeight="700" textAnchor="middle" style={{ pointerEvents: 'none', letterSpacing: '-0.02em' }}>
              <text x="100" y="44" fill={textFill('front')}>전진</text>
              <text x="41" y="105" fill={textFill('left')}>좌회전</text>
              <text x="100" y="166" fill={textFill('back')}>후진</text>
              <text x="159" y="105" fill={textFill('right')}>우회전</text>
              <text x="100" y="105.5" fill={active !== null ? '#ffffff' : '#e11d48'} fontSize="14.5" fontWeight="800">정지</text>
            </g>

            {(mode === 'walk' || mode === 'roll') && (
              <g fontSize="7.5" fontWeight="600" textAnchor="middle" style={{ pointerEvents: 'none' }}>
                <text x="100" y="17" fill={subTextFill('front')}>빠르게 {SPEED_MAX}</text>
                <text x="100" y="61" fill={subTextFill('front')}>느리게 {SPEED_MIN}</text>
                <text x="100" y="146" fill={subTextFill('back')}>느리게 {SPEED_MIN}</text>
                <text x="100" y="190" fill={subTextFill('back')}>빠르게 {SPEED_MAX}</text>
              </g>
            )}
          </svg>
        </div>
      </div>

      <div className="otto-status-wrap">
        <div className={`otto-status-badge ${active || aiOn ? 'is-active' : ''}`}>
          <span className="otto-status-indicator" />
          <span>{statusText}</span>
        </div>
      </div>

      {aiError && (
        <div style={{ marginTop: '14px', padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '14px', color: '#b91c1c', fontSize: '0.9rem', textAlign: 'center', fontWeight: 500 }}>
          {aiError}
        </div>
      )}

      <div className="otto-guide-box">
        <div className="otto-guide-line">
          구역을 누르면 정지할 때까지 계속 움직입니다. 가운데 <b>[정지]</b> 원을 누르면 멈춥니다.
        </div>
        {(mode === 'walk' || mode === 'roll') && (
          <div className="otto-guide-line">
            전진·후진은 가운데에 가까울수록 느리게({SPEED_MIN}), 바깥 원주일수록 빠르게({SPEED_MAX}) 움직입니다. (보통 속도 100) 누른 채 끌어 속도를 고르고 손을 떼면 그 속도로 바뀝니다.
          </div>
        )}
      </div>

      {(aiOn || chatLog.length > 0) && (
        <div className="otto-subpanel" style={{ marginTop: '20px', marginBottom: 0 }}>
          <div className="otto-subpanel-title">AI 대화</div>
          <div style={{
            maxHeight: '260px', overflowY: 'auto', border: '1px solid #f1f5f9',
            borderRadius: '14px', padding: '14px', background: '#f8fafc',
            display: 'flex', flexDirection: 'column', gap: '10px'
          }}>
            {chatLog.length === 0 && (
              <div style={{ color: '#94a3b8', fontSize: '0.9rem', textAlign: 'center', padding: '16px 0' }}>
                로봇에게 말을 걸면 대화가 여기에 나타납니다.
              </div>
            )}
            {chatLog.map((line, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {line.user && (
                  <div style={{ alignSelf: 'flex-end', background: '#2563eb', color: '#ffffff', padding: '8px 14px', borderRadius: '14px 14px 4px 14px', fontSize: '0.9rem', maxWidth: '85%' }}>
                    <b style={{ opacity: 0.85, marginRight: '6px', fontSize: '0.8rem' }}>나</b> {line.user}
                  </div>
                )}
                {line.reply && (
                  <div style={{ alignSelf: 'flex-start', background: '#ffffff', color: '#1e293b', border: '1px solid #e2e8f0', padding: '8px 14px', borderRadius: '14px 14px 14px 4px', fontSize: '0.9rem', maxWidth: '85%', boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
                    <b style={{ color: '#2563eb', marginRight: '6px', fontSize: '0.8rem' }}>Otto</b> {line.reply}
                  </div>
                )}
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>
          <div style={{ marginTop: '8px', fontSize: '0.8rem', color: '#94a3b8' }}>
            긴 말은 화면에서 줄여서 보여 줍니다. 음성은 전체가 재생됩니다.
          </div>
        </div>
      )}
    </div>
  );
};

export default OttoPad;
